import React, { useState } from "react";

/**
 * This component is intended for internal use and should remain invisible to the user.
 * It is specifically used during the generation of screenshots to highlight UI elements
 * with either a circle or an arrow, or both.
 *
 * The Highlighter works by highlighting the element that was clicked, but only if the mouse
 * button is held down for an extended period, making it unlikely for the user to notice.
 *
 * Even if the user does discover this feature, it’s harmless. It simply shows a
 * semi-transparent overlay that does not affect app functionality.
 *
 * To use the Highlighter, wrap the target element within it. The highlight will activate
 * if the mouse-down event is sustained over the wrapped element for a certain duration.
 *
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The content to be highlighted.
 * @param {boolean} props.useCircle - Flag to use circle or not.
 * @param {boolean} props.useArrow - Flag to use arrow or not.
 * @param {string} props.arrowBottom - The bottom position of the arrow.
 * @param {string} props.arrowLeft - The left position of the arrow.
 * @param {string} props.arrowTransform - The transform property for the arrow.
 * @param {string} props.circleTop - The top position of the circle.
 * @param {string} props.circleLeft - The left position of the circle.
 * @param {string} props.circleTransform - The transform property for the circle.
 * @param {string} props.circleWidth - The width of the circle.
 * @param {string} props.circleHeight - The height of the circle.
 */
const Highlighter = ({
  children,
  arrowBottom,
  arrowLeft,
  arrowTransform,
  circleTop,
  circleLeft,
  circleTransform,
  circleWidth,
  circleHeight,
  useCircle = true, // New prop for circle
  useArrow = true, // New prop for arrow
}) => {
  const [isHighlighted, setIsHighlighted] = useState(false);
  let timer;

  const handleMouseDown = () => {
    timer = setTimeout(() => {
      setIsHighlighted(true);
    }, 3000); // Activate after 3 seconds
  };

  const handleMouseUp = () => {
    clearTimeout(timer);
    setIsHighlighted(false); // Reset on mouse up
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      style={{ position: "relative" }}
      // style={{ position: 'relative', display: 'inline-block' }}
    >
      {isHighlighted && (
        <>
          {useCircle && ( // Conditional rendering for circle
            <div
              className="highlight-circle"
              style={{
                content: '""',
                position: "absolute",
                top: circleTop || "50%",
                left: circleLeft || "50%",
                transform: circleTransform || "translate(-50%, -50%)",
                width: circleWidth || "calc(100% + 20px)",
                height: circleHeight || "calc(100% + 20px)",
                borderRadius: "50%",
                border: "7px solid rgba(255, 0, 0, 0.6)",
                boxSizing: "border-box",
                opacity: 1,
                transition: "opacity 0.3s ease",
                pointerEvents: "none",
              }}
            />
          )}
          {useArrow && ( // Conditional rendering for arrow
            <div
              className="highlight-arrow"
              style={{
                content: '""',
                position: "absolute",
                bottom: arrowBottom || "-180%",
                left: arrowLeft || "-35px",
                transform: arrowTransform || "rotate(-55deg)",
                width: "60px",
                height: "60px",
                backgroundColor: "rgba(255, 0, 0, 0.6)",
                clipPath:
                  "polygon(0% 25%, 50% 25%, 50% 0%, 100% 50%, 50% 100%, 50% 75%, 0% 75%)",
                opacity: 1,
                transition: "opacity 0.3s ease",
                pointerEvents: "none",
              }}
            />
          )}
        </>
      )}
      {children(isHighlighted)} {/* Pass isHighlighted to children */}
    </div>
  );
};

export default Highlighter;
